import React from 'react';
import { RadialBarChart, RadialBar, PolarAngleAxis } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../../elements/ui/card';
import { Badge } from '../../../elements/ui/badge';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface GaugeThreshold {
  label: string;
  max: number;
  color: string;
  badgeVariant: 'success' | 'warning' | 'orange' | 'danger' | 'blue';
}

interface RadialGaugeProps {
  title: string;
  value: number | null;
  max: number;
  unit: string;
  thresholds: GaugeThreshold[];
}

const RadialGauge: React.FC<RadialGaugeProps> = ({ title, value, max, unit, thresholds }) => {
  const hasData = value != null;
  const { t } = useTranslation();
  const percentage = hasData ? Math.min((value / max) * 100, 100) : 0;

  const currentThreshold = hasData
    ? (thresholds.find((t) => value <= t.max) ?? thresholds[thresholds.length - 1])
    : null;

  const gaugeColor = currentThreshold?.color ?? '#e5e7eb';

  // Build scale numbers: 0, each threshold boundary, and max (deduplicated)
  const scaleNumbers = [0, ...thresholds.map((t) => t.max)];
  if (scaleNumbers[scaleNumbers.length - 1] !== max) {
    scaleNumbers.push(max);
  }
  const uniqueScale = [...new Set(scaleNumbers)].sort((a, b) => a - b);

  return (
    <Card className="h-full">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-center text-xs">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center pb-4 px-4">
        {!hasData ? (
          <div className="flex flex-col items-center justify-center h-[120px]">
            <svg width="100" height="100" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="8" strokeDasharray="4 4" />
            </svg>
            <AlertTriangle className="h-5 w-5 text-gray-300 -mt-14" />
            <p className="text-xs text-gray-400 mt-10">{t('f-no-data')}</p>
          </div>
        ) : (
          <>
            <div className="relative">
              <RadialBarChart
                width={130}
                height={130}
                cx={65}
                cy={65}
                innerRadius={45}
                outerRadius={58}
                barSize={10}
                data={[{ value: percentage }]}
                startAngle={225}
                endAngle={-45}
              >
                <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                <RadialBar background={{ fill: '#f3f4f6' }} dataKey="value" cornerRadius={5} fill={gaugeColor} />
              </RadialBarChart>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-bold text-gray-900">{value}</span>
                <span className="text-xs text-gray-500">{unit}</span>
              </div>
            </div>

            {currentThreshold && (
              <Badge variant={currentThreshold.badgeVariant} className="mt-1">
                {currentThreshold.label}
              </Badge>
            )}

            {/* Threshold bar with numbered scale */}
            <div className="w-full mt-3 px-1">
              <div className="flex h-1.5 rounded-full overflow-hidden">
                {thresholds.map((t, i) => {
                  const prevMax = thresholds[i - 1]?.max ?? 0;
                  const widthPct = ((t.max - prevMax) / max) * 100;
                  return (
                    <div
                      key={t.label}
                      className="h-full"
                      style={{
                        backgroundColor: t.color,
                        width: `${widthPct}%`,
                        opacity: currentThreshold === t ? 1 : 0.3
                      }}
                    />
                  );
                })}
              </div>
              {/* Numbered scale */}
              <div className="relative h-3 mt-0.5">
                {uniqueScale.map((n) => (
                  <span
                    key={n}
                    className="absolute text-[10px] text-gray-400 -translate-x-1/2"
                    style={{ left: `${(n / max) * 100}%` }}
                  >
                    {n}
                  </span>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1 justify-center">
              {thresholds.map((t) => (
                <div key={t.label} className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.color }} />
                  <span className="text-[10px] text-gray-500">{t.label}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RadialGauge;
